<template>
  <div class="text-base font-normal leading-5">
    <div
      class="mb-2 flex items-center justify-between gap-4 border-b border-solid border-black-10 pb-1.5 leading-6 md:pb-2"
    >
      <div v-if="sizeNote" class="flex gap-1">
        <IconMeasuringTape class="h-5 w-7 shrink-0" />
        <p class="text-base">
          {{ $t(sizeNote) }}
        </p>
      </div>
      <span v-else>{{ $t('pdp.size_picker.choose_size') }}:</span>
      <SizeGuide hide-icon />
    </div>
    <div>
      <div class="flex">
        <button
          v-for="(category, idx) in formatedSizeCategories"
          :key="idx"
          class="border-b px-4 py-2 focus:ring-2"
          :class="{
            'border-b-white bg-white font-bold': activeTab === idx,
            'border-black-10 bg-black-5': activeTab !== idx,
          }"
          @click="activeTab = idx"
        >
          {{ $t(`pdp.size_picker.${category.categoryName}`) }}
        </button>
      </div>
      <div class="mt-2">
        <div
          v-for="(category, idx) in formatedSizeCategories"
          v-show="activeTab === idx"
          :key="idx"
        >
          <div class="flex flex-wrap gap-2">
            <SizeVariant
              v-for="size in category.sizes"
              :key="size.id"
              :variant="size.variant"
              :active="
                selectedVariant && selectedVariant.id === size.variant.id
              "
              @select-size="handleSelectedSize"
            />
          </div>
        </div>
      </div>
    </div>
    <ProductSizeGuideModal
      v-if="isProductSizeGuideModalOpen"
      :product="product"
    />
  </div>
</template>

<script setup lang="ts">
import { isInStock } from '@scayle/storefront-nuxt'
import type { Product, Variant } from '@scayle/storefront-nuxt'
import SizeVariant from './SizeVariant.vue'
import SizeGuide from './SizeGuide.vue'
import useUiState from '~/composables/ui/useUiState'
import { getSizeNote } from '~/utils/product'

const props = defineProps<{
  product: Product
  variants: Variant[]
  selectedVariant?: Variant
}>()

const emit = defineEmits<{
  (e: 'preSelectSize' | 'selectSize', variant: Variant): void
}>()

const { setActiveVariant } = useUiState()
const { isProductSizeGuideModalOpen } = useUiState()

const activeTab = ref(0)
const sizeNote = computed(() => getSizeNote(props.product))
const variantsInStock = computed((): Variant[] => {
  return props.variants.filter((variant) => isInStock(variant))
})

const sizeCategories = computed(() =>
  getProductSizesFromVariants(props.variants),
)

const formatedSizeCategories = computed(() =>
  Object.entries(sizeCategories.value).map(([categoryName, sizes]) => ({
    categoryName,
    sizes,
  })),
)

watch(
  variantsInStock,
  (newValue) => {
    // Automatically select variant if it is the only one in stock
    if (newValue.length === 1) {
      const variant = newValue[0]
      if (!variant) {
        return
      }
      setActiveVariant(variant)
      emit('preSelectSize', variant)
      emit('selectSize', variant)
    } else {
      setActiveVariant(undefined)
    }
  },
  { immediate: true },
)

watch(
  () => props.selectedVariant,
  (variant) => {
    if (!variant) {
      return
    }
    const tabIndex = formatedSizeCategories.value.findIndex((category) =>
      category.sizes.some((size) => size.variant.id === variant.id),
    )
    if (tabIndex !== -1) {
      activeTab.value = tabIndex
    }
  },
  { immediate: true },
)

const handleSelectedSize = (variant: Variant): void => {
  emit('selectSize', variant)
}
</script>
